import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import type { Bags, SegmentType } from '@shared/types.ts';
import type { ExtractedSegment } from '@shared/ingest/extracted.ts';
import { TYPE_LABEL } from './segments.ts';
import { WhoField } from './Who.tsx';

type Draft = Partial<ExtractedSegment>;

let focusNext: string | undefined;

/** Open the form scrolled to one field (from a "Not in the email · Add" row). */
export function focusFieldOnOpen(fieldId: string): void {
  focusNext = fieldId;
}

export const detailFieldId = (key: string) => `sf-detail-${key.replace(/\W+/g, '-')}`;

/** Empty bag fields mean "the email didn't say", so an all-empty allowance is no allowance. */
function cleanBags(b: Bags | undefined): Bags | undefined {
  if (!b) return undefined;
  const out: Bags = {};
  if (b.carryOn) out.carryOn = b.carryOn;
  if (b.checked !== undefined && !Number.isNaN(b.checked)) out.checked = b.checked;
  if (b.extraBagFee?.trim()) out.extraBagFee = b.extraBagFee.trim();
  return Object.keys(out).length ? out : undefined;
}

function Field({ id, label, children, invalid, help }: { id: string; label: string; children: ReactNode; invalid?: boolean; help?: string }) {
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      {children}
      {invalid && (
        <span className="error" id={`${id}-err`}>
          Required
        </span>
      )}
      {help && <span className="help">{help}</span>}
    </div>
  );
}

/** Add / edit / fix one booking item. Validation itself happens in the store (same rules as email). */
export function SegmentForm({
  initial,
  submitLabel,
  onSubmit,
  secondary,
  problems: externalProblems = [],
}: {
  initial?: Draft;
  submitLabel: string;
  onSubmit: (seg: ExtractedSegment) => string[];
  secondary?: ReactNode;
  problems?: string[];
}) {
  const [d, setD] = useState<Draft>(() => ({ type: 'stay', start: { date: '' }, ...structuredClone(initial ?? {}) }));
  const [problems, setProblems] = useState<string[]>(externalProblems);
  const type = (d.type ?? 'stay') as SegmentType;
  const missing = (label: string) => problems.some((p) => p.toLowerCase().includes(label.toLowerCase()));

  const set = (patch: Draft) => setD((cur) => ({ ...cur, ...patch }));
  const setStart = (patch: Partial<ExtractedSegment['start']>) => setD((cur) => ({ ...cur, start: { date: '', ...cur.start, ...patch } }));
  const setEnd = (patch: Partial<ExtractedSegment['start']>) => setD((cur) => ({ ...cur, end: { date: '', ...cur.end, ...patch } }));
  const setPlace = (key: 'place' | 'from' | 'to', patch: Record<string, string>) => setD((cur) => ({ ...cur, [key]: { ...cur[key], ...patch } }));
  const setBags = (patch: Partial<Bags>) => setD((cur) => ({ ...cur, bags: { ...cur.bags, ...patch } }));
  const setDetail = (key: string, value: string) => setD((cur) => ({ ...cur, details: { ...cur.details, [key]: value } }));

  useEffect(() => {
    const id = focusNext;
    focusNext = undefined;
    const el = id ? document.getElementById(id) : null;
    if (!el) return;
    el.scrollIntoView({ block: 'center' });
    el.focus({ preventScroll: true });
  }, []);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const details = Object.fromEntries(Object.entries(d.details ?? {}).filter(([, v]) => v?.trim()));
    const seg: ExtractedSegment = {
      ...(d as ExtractedSegment),
      type,
      start: { date: d.start?.date ?? '', time: d.start?.time || undefined, timeZone: d.start?.timeZone },
      end: d.end?.date ? { date: d.end.date, time: d.end.time || undefined, timeZone: d.end.timeZone } : undefined,
      travelers: (d.travelers ?? []).filter(Boolean),
      seats: (d.seats ?? []).filter(Boolean),
      confirmation: d.confirmation?.trim() || undefined,
      provider: d.provider?.trim() || undefined,
      fare: d.fare?.trim() || undefined,
      price: d.price?.trim() || undefined,
      bags: type === 'flight' ? cleanBags(d.bags) : undefined,
      notes: d.notes?.trim() || undefined,
      who: d.who?.length ? d.who : undefined,
      details,
    };
    const result = onSubmit(seg);
    setProblems(result);
  };

  const isTransit = type === 'flight' || type === 'train';
  const startLabel = { flight: 'Departure', train: 'Departure', stay: 'Check-in', car: 'Pick-up', reservation: 'Date', activity: 'Date' }[type];
  const endLabel = { flight: 'Arrival', train: 'Arrival', stay: 'Check-out', car: 'Drop-off', reservation: '', activity: 'Ends' }[type];

  return (
    <form className="stack-lg" onSubmit={submit} noValidate>
      {problems.length > 0 && (
        <div className="banner banner-orange" role="alert">
          <span className="small" style={{ fontWeight: 500 }}>
            {problems.join(' · ')}
          </span>
        </div>
      )}

      <Field id="sf-type" label="Type">
        <select id="sf-type" className="select" value={type} onChange={(e) => set({ type: e.target.value as SegmentType })}>
          {(Object.keys(TYPE_LABEL) as SegmentType[]).map((t) => (
            <option key={t} value={t}>
              {TYPE_LABEL[t]}
            </option>
          ))}
        </select>
      </Field>

      {isTransit ? (
        <div className="grid-2">
          <Field id="sf-from" label={type === 'flight' ? 'From (airport code)' : 'From station'} invalid={missing('departure airport') || missing('departure station')}>
            <input
              id="sf-from"
              className="input"
              value={(type === 'flight' ? d.from?.code : d.from?.name) ?? ''}
              onChange={(e) => setPlace('from', type === 'flight' ? { code: e.target.value.toUpperCase() } : { name: e.target.value })}
              placeholder={type === 'flight' ? 'JFK' : 'Lisboa Oriente'}
              autoCapitalize="characters"
              maxLength={type === 'flight' ? 3 : undefined}
            />
          </Field>
          <Field id="sf-to" label={type === 'flight' ? 'To (airport code)' : 'To station'} invalid={missing('arrival airport') || missing('arrival station')}>
            <input
              id="sf-to"
              className="input"
              value={(type === 'flight' ? d.to?.code : d.to?.name) ?? ''}
              onChange={(e) => setPlace('to', type === 'flight' ? { code: e.target.value.toUpperCase() } : { name: e.target.value })}
              placeholder={type === 'flight' ? 'LIS' : 'Porto Campanhã'}
              autoCapitalize="characters"
              maxLength={type === 'flight' ? 3 : undefined}
            />
          </Field>
        </div>
      ) : type === 'activity' ? (
        <Field id="sf-title" label="What" invalid={missing('what it is')}>
          <input id="sf-title" className="input" value={d.title ?? ''} onChange={(e) => set({ title: e.target.value })} placeholder="Day trip to Sintra" />
        </Field>
      ) : (
        <Field id="sf-place" label={type === 'car' ? 'Pick-up location' : 'Place'} invalid={missing('place') || missing('pick-up location')}>
          <input id="sf-place" className="input" value={d.place?.name ?? ''} onChange={(e) => setPlace('place', { name: e.target.value })} />
        </Field>
      )}

      {!isTransit && type !== 'activity' && (
        <div className="grid-2">
          <Field id="sf-city" label="City">
            <input id="sf-city" className="input" value={d.place?.city ?? ''} onChange={(e) => setPlace('place', { city: e.target.value })} />
          </Field>
          <Field id="sf-addr" label="Address">
            <input id="sf-addr" className="input" value={d.place?.address ?? ''} onChange={(e) => setPlace('place', { address: e.target.value })} />
          </Field>
        </div>
      )}

      <div className="grid-2">
        <Field id="sf-sd" label={`${startLabel} date`} invalid={missing(`${startLabel.toLowerCase()} date`) || (type === 'reservation' && missing('missing: date'))}>
          <input
            id="sf-sd"
            type="date"
            className="input"
            value={d.start?.date ?? ''}
            onChange={(e) => setStart({ date: e.target.value })}
            aria-invalid={missing(`${startLabel.toLowerCase()} date`) || undefined}
          />
        </Field>
        <Field id="sf-st" label={`${startLabel} time`} invalid={missing(`${startLabel.toLowerCase()} time`) || (type === 'reservation' && missing('missing: time'))}>
          <input id="sf-st" type="time" className="input" value={d.start?.time ?? ''} onChange={(e) => setStart({ time: e.target.value })} />
        </Field>
      </div>

      {endLabel && (
        <div className="grid-2">
          <Field id="sf-ed" label={`${endLabel} date`} invalid={missing(`${endLabel.toLowerCase()} date`)}>
            <input id="sf-ed" type="date" className="input" value={d.end?.date ?? ''} onChange={(e) => setEnd({ date: e.target.value })} />
          </Field>
          <Field id="sf-et" label={`${endLabel} time`} invalid={missing(`${endLabel.toLowerCase()} time`)}>
            <input id="sf-et" type="time" className="input" value={d.end?.time ?? ''} onChange={(e) => setEnd({ time: e.target.value })} />
          </Field>
        </div>
      )}

      <div className="grid-2">
        <Field id="sf-conf" label="Confirmation code" invalid={missing('confirmation')}>
          <input id="sf-conf" className="input" value={d.confirmation ?? ''} onChange={(e) => set({ confirmation: e.target.value.toUpperCase() })} autoCapitalize="characters" />
        </Field>
        {isTransit ? (
          <Field id="sf-num" label={type === 'flight' ? 'Flight number' : 'Train number'}>
            <input id="sf-num" className="input" value={d.number ?? ''} onChange={(e) => set({ number: e.target.value.toUpperCase() })} placeholder={type === 'flight' ? 'TP 202' : ''} />
          </Field>
        ) : (
          <Field id="sf-prov" label={type === 'car' ? 'Rental company' : 'Booked through'}>
            <input id="sf-prov" className="input" value={d.provider ?? ''} onChange={(e) => set({ provider: e.target.value })} />
          </Field>
        )}
      </div>

      {type === 'flight' && (
        <>
          <div className="grid-2">
            <Field id="sf-prov" label="Airline">
              <input id="sf-prov" className="input" value={d.provider ?? ''} onChange={(e) => set({ provider: e.target.value })} placeholder="JetBlue" />
            </Field>
            <Field id="sf-op" label="Operated by">
              <input id="sf-op" className="input" value={d.details?.['Operated by'] ?? ''} onChange={(e) => setDetail('Operated by', e.target.value)} />
            </Field>
          </div>
          <div className="grid-2">
            <Field id="sf-from-term" label="Departure terminal">
              <input id="sf-from-term" className="input" value={d.from?.terminal ?? ''} onChange={(e) => setPlace('from', { terminal: e.target.value })} />
            </Field>
            <Field id="sf-to-term" label="Arrival terminal">
              <input id="sf-to-term" className="input" value={d.to?.terminal ?? ''} onChange={(e) => setPlace('to', { terminal: e.target.value })} />
            </Field>
          </div>
        </>
      )}

      <WhoField value={d.who ?? []} onChange={(who) => set({ who })} />

      <Field id="sf-trav" label="Names on booking" help="As printed on the booking, separated by commas">
        <input id="sf-trav" className="input" value={(d.travelers ?? []).join(', ')} onChange={(e) => set({ travelers: e.target.value.split(',').map((s) => s.trim()) })} />
      </Field>

      {type === 'flight' && (
        <Field id="sf-seats" label="Seats">
          <input id="sf-seats" className="input" value={(d.seats ?? []).join(', ')} onChange={(e) => set({ seats: e.target.value.split(',').map((s) => s.trim().toUpperCase()) })} placeholder="23A, 23B" />
        </Field>
      )}

      {isTransit && (
        <Field id="sf-fare" label="Fare / cabin">
          <input id="sf-fare" className="input" value={d.fare ?? ''} onChange={(e) => set({ fare: e.target.value })} placeholder="Main Base / Main" />
        </Field>
      )}

      {type === 'flight' && (
        <fieldset className="field-group" id="sf-bags">
          <legend>Bags per traveler</legend>
          <div className="grid-2">
            <Field id="sf-carry" label="Carry-on">
              <select
                id="sf-carry"
                className="select"
                value={d.bags?.carryOn ?? ''}
                onChange={(e) => setBags({ carryOn: (e.target.value || undefined) as Bags['carryOn'] })}
              >
                <option value="">Not stated</option>
                <option value="included">Included</option>
                <option value="fee">Costs extra</option>
                <option value="none">Not included</option>
              </select>
            </Field>
            <Field id="sf-checked" label="Checked bags included">
              <input
                id="sf-checked"
                className="input"
                inputMode="numeric"
                value={d.bags?.checked ?? ''}
                onChange={(e) => setBags({ checked: e.target.value === '' ? undefined : Number(e.target.value.replace(/\D/g, '')) })}
                placeholder="0"
              />
            </Field>
          </div>
          <Field id="sf-bagfee" label="Next checked bag costs">
            <input id="sf-bagfee" className="input" value={d.bags?.extraBagFee ?? ''} onChange={(e) => setBags({ extraBagFee: e.target.value })} placeholder="$49" />
          </Field>
        </fieldset>
      )}

      <Field id="sf-price" label="Price paid (whole booking)">
        <input id="sf-price" className="input" value={d.price ?? ''} onChange={(e) => set({ price: e.target.value })} placeholder="$406.80" />
      </Field>

      {Object.keys(d.details ?? {})
        .filter((k) => k !== 'Operated by')
        .map((k) => (
          <Field key={k} id={detailFieldId(k)} label={k}>
            <input id={detailFieldId(k)} className="input" value={d.details?.[k] ?? ''} onChange={(e) => setDetail(k, e.target.value)} />
          </Field>
        ))}

      <Field id="sf-notes" label="Notes">
        <textarea id="sf-notes" className="textarea" value={d.notes ?? ''} onChange={(e) => set({ notes: e.target.value })} />
      </Field>

      <div className="row" style={{ gap: 10 }}>
        <button type="submit" className="btn btn-primary grow">
          {submitLabel}
        </button>
        {secondary}
      </div>
    </form>
  );
}
